import React, { createContext, useState, useEffect, useContext } from 'react';
import { User, onAuthStateChanged, getAuth as getFirebaseAuth } from 'firebase/auth';
import { auth, initializeFirebase } from '../firebase/config';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  initialized: boolean; // Add this to track if Firebase is ready
}

const AuthContext = createContext<AuthContextType>({ user: null, loading: true, initialized: false });

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let initTimeout: NodeJS.Timeout | undefined;

    const initAuth = async () => {
      try {
        // Set a timeout to prevent hanging indefinitely
        const initPromise = initializeFirebase();
        const timeoutPromise = new Promise((_, reject) => {
          initTimeout = setTimeout(() => reject(new Error('Firebase initialization timeout')), 15000);
        });

        // Race between initialization and timeout
        await Promise.race([initPromise, timeoutPromise]);
        if (initTimeout) clearTimeout(initTimeout);
        
        setInitialized(true);
        console.log('Firebase initialized successfully in AuthContext');
        
        // Now set up the auth listener
        const firebaseAuth = auth ?? getFirebaseAuth();
        unsubscribe = onAuthStateChanged(firebaseAuth, (currentUser) => {
          console.log('Auth state changed:', currentUser?.email);
          setUser(currentUser);
          setLoading(false);
        });

      } catch (error) {
        console.error('Auth initialization error:', error);
        if (initTimeout) clearTimeout(initTimeout);
        setLoading(false);
        setInitialized(true); // Set as initialized even on error so app can render
      }
    };

    initAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, initialized }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };